import { useRef, useEffect } from 'react';

export interface SuggestionItem {
  label: string;
  sub?: string;
  type: string;
  icon: string;
  value: string;
}

interface Props {
  search: string;
  onSearchChange: (v: string) => void;
  onClear: () => void;
  suggestions: SuggestionItem[];
  onSuggestionClick: (item: SuggestionItem) => void;
  placeholder?: string;
  showDropdown: boolean;
  onShowDropdownChange: (v: boolean) => void;
  inputClassName?: string;
}

const SearchDropdown = ({
  search,
  onSearchChange,
  onClear,
  suggestions,
  onSuggestionClick,
  placeholder = '搜索...',
  showDropdown,
  onShowDropdownChange,
  inputClassName = '',
}: Props) => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        onShowDropdownChange(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onShowDropdownChange]);

  const grouped = suggestions.reduce<Record<string, SuggestionItem[]>>((acc, item) => {
    if (!acc[item.type]) acc[item.type] = [];
    acc[item.type].push(item);
    return acc;
  }, {});

  const typeLabels: Record<string, string> = {
    name: '导师 / 讲师',
    company: '公司',
    skill: '技能 / 标签',
    course: '课程',
    category: '分类',
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center pointer-events-none">
          <i className="ri-search-line text-slate-400 text-base" />
        </div>
        <input
          type="text"
          value={search}
          onChange={(e) => {
            onSearchChange(e.target.value);
            onShowDropdownChange(true);
          }}
          onFocus={() => { if (search) onShowDropdownChange(true); }}
          placeholder={placeholder}
          className={`w-full pl-11 pr-10 py-3.5 rounded-xl border text-sm transition-all ${inputClassName}`}
        />
        {search && (
          <button
            onClick={() => { onClear(); onShowDropdownChange(false); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer text-slate-400 hover:text-white transition-colors"
          >
            <i className="ri-close-line text-base" />
          </button>
        )}
      </div>

      {showDropdown && suggestions.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl border border-slate-200 overflow-hidden z-50 max-h-80 overflow-y-auto">
          {Object.entries(grouped).map(([type, items]) => (
            <div key={type}>
              <div className="px-4 py-2 bg-slate-50 border-b border-slate-100">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  {typeLabels[type] || type}
                </span>
              </div>
              {items.map((item, i) => (
                <button
                  key={i}
                  onMouseDown={() => {
                    onSuggestionClick(item);
                    onShowDropdownChange(false);
                  }}
                  className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-violet-50 transition-colors cursor-pointer group"
                >
                  <div className="w-7 h-7 flex items-center justify-center bg-slate-100 group-hover:bg-violet-100 rounded-lg flex-shrink-0 transition-colors">
                    <i className={`${item.icon} text-slate-500 group-hover:text-violet-600 text-sm transition-colors`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-700 group-hover:text-violet-700 transition-colors truncate">
                      {item.label}
                    </p>
                    {item.sub && (
                      <p className="text-xs text-slate-400 truncate">{item.sub}</p>
                    )}
                  </div>
                  <i className="ri-arrow-right-up-line text-slate-300 group-hover:text-violet-400 text-sm flex-shrink-0 transition-colors" />
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchDropdown;
